/**
 * Rubata spudoratamente dalla fetchBaseQuery di rtk query, furto is the way!
 *
 * Per info sull'originale: https://github.com/reduxjs/redux-toolkit/blob/master/packages/toolkit/src/query/fetchBaseQuery.ts
 *
 * Abbiamo applicato solo alcune modifiche per renderla conforme alle nostre esigenze
 */
import { Err, Ok, Result } from "@pacote/result"
import { prettifyError, ZodObject, ZodString } from "zod"

import isPlainObject from "@/lib/is-plain-object"
import { joinUrls } from "@/lib/join-urls"
import { MaybePromise, Override } from "@/types/ts-helpers"

import {
  ApiFailure,
  baseApiErrorResponseSchema,
  baseApiResponseSchema,
  UnhandledApiError,
} from "./api-config"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

type ResponseHandler =
  | "content-type"
  | "json"
  | "text"
  | ((response: Response) => Promise<any>)

type CustomRequestInit = Override<
  RequestInit,
  {
    headers?:
      | Headers
      | string[][]
      | Record<string, string | undefined>
      | undefined
  }
>

type PrepareHeadersOptions = {
  endpoint: string
}

interface FetcherArgs extends CustomRequestInit {
  url: string
  params?: Record<string, any>
  body?: any
  responseHandler?: ResponseHandler
  validateStatus?: (response: Response, body: any) => boolean
  /**
   * A number in milliseconds that represents that maximum time a request can take before timing out.
   */
  timeout?: number
  successSchemaValidator?: ZodObject<any> | ZodString
  errorSchemaValidator?: ZodObject<any> | ZodString
  throwOnValidationError?: boolean
}

type CreateFetcherArgs = {
  baseUrl?: string
  prepareHeaders?: (
    headers: Headers,
    options: PrepareHeadersOptions,
  ) => MaybePromise<Headers>
  prepareBody?: (body: RequestInit["body"]) => MaybePromise<RequestInit["body"]>
  prepareParams?: (
    params: Record<string, any>,
  ) => MaybePromise<Record<string, any>>
  fetchFn?: (
    input: RequestInfo,
    init?: RequestInit | undefined,
  ) => Promise<Response>
  jsonContentType?: string
} & RequestInit &
  Pick<FetcherArgs, "responseHandler" | "validateStatus" | "timeout">

type FetchBaseQueryMeta = { request: Request; response?: Response }

type FetcherErrorResponse<E> = {
  status: number
  data: E
  meta: FetchBaseQueryMeta
}

/* -------------------------------------------------------------------------------------------------
 * utils
 * -----------------------------------------------------------------------------------------------*/

function stripUndefined(obj: any) {
  if (!isPlainObject(obj)) {
    return obj
  }
  const copy: Record<string, any> = { ...obj }
  for (const [k, v] of Object.entries(copy)) {
    if (v === undefined) delete copy[k]
  }
  return copy
}

const isJsonContentType = (headers: Headers) =>
  /*applicat*/ /ion\/(vnd\.api\+)?json/.test(headers.get("content-type") || "")

const defaultFetchFn: typeof fetch = (...args) => fetch(...args)

const defaultValidateStatus = (response: Response) =>
  response.status >= 200 && response.status <= 299

/* -------------------------------------------------------------------------------------------------
 * create base fetcher
 * -----------------------------------------------------------------------------------------------*/

function createFetcher({
  baseUrl,
  prepareHeaders = (x) => x,
  fetchFn = defaultFetchFn,
  validateStatus: globalValidateStatus,
  timeout: defaultTimeout,
  jsonContentType = "application/json",
  responseHandler: globalResponseHandler,
  ...baseFetchOptions
}: CreateFetcherArgs) {
  async function handleResponse(
    response: Response,
    responseHandler: ResponseHandler,
  ) {
    if (typeof responseHandler === "function") {
      return responseHandler(response)
    }

    if (responseHandler === "content-type") {
      responseHandler = isJsonContentType(response.headers) ? "json" : "text"
    }

    if (responseHandler === "json") {
      const text = await response.text()
      return text.length ? JSON.parse(text) : null
    }

    return response.text()
  }

  return async function fetcher<S, E>(
    arg: string | FetcherArgs,
  ): Promise<Result<S, FetcherErrorResponse<E>>> {
    let {
      url,
      headers = new Headers(baseFetchOptions.headers),
      /* eslint-disable prefer-const */
      params = undefined,
      responseHandler = globalResponseHandler ?? ("json" as const),
      validateStatus = globalValidateStatus ?? defaultValidateStatus,
      timeout = defaultTimeout,
      successSchemaValidator = baseApiResponseSchema,
      errorSchemaValidator = baseApiErrorResponseSchema,
      throwOnValidationError = false,
      ...rest
      /* eslint-enable prefer-const */
    } = typeof arg == "string" ? { url: arg } : arg

    const config: RequestInit = {
      ...baseFetchOptions,
      ...rest,
    }

    headers = new Headers(stripUndefined(headers))
    config.headers =
      (await prepareHeaders(headers, {
        endpoint: url,
      })) || headers

    const isJsonifiable = (body: any) =>
      typeof body === "object" &&
      (isPlainObject(body) ||
        Array.isArray(body) ||
        typeof body.toJSON === "function")

    if (!config.headers.has("content-type") && isJsonifiable(config.body)) {
      config.headers.set("content-type", jsonContentType)
    }

    if (isJsonifiable(config.body) && isJsonContentType(config.headers)) {
      config.body = JSON.stringify(config.body)
    }

    if (params) {
      const divider = ~url.indexOf("?") ? "&" : "?"
      const query = new URLSearchParams(stripUndefined(params))
      url += divider + query
    }

    url = joinUrls(baseUrl, url)
    let response,
      timedOut = false
    const timeoutId =
      timeout &&
      setTimeout(() => {
        timedOut = true
      }, timeout)

    const request = new Request(url, config)
    const requestClone = new Request(url, config)
    const meta: FetchBaseQueryMeta = { request: requestClone }

    try {
      response = await fetchFn(request)
    } catch (e) {
      throw new UnhandledApiError({
        status: timedOut ? ApiFailure.TIMEOUT_ERROR : ApiFailure.FETCH_ERROR,
        message: String(e),
        meta,
      })
    } finally {
      if (timeoutId) clearTimeout(timeoutId)
    }

    const responseClone = response.clone()

    meta.response = responseClone

    let resultData: any
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let responseText: string = ""
    try {
      let handleResponseError
      await Promise.all([
        handleResponse(response, responseHandler).then(
          (r) => (resultData = r),
          (e) => (handleResponseError = e),
        ),
        // see https://github.com/node-fetch/node-fetch/issues/665#issuecomment-538995182
        // we *have* to "use up" both streams at the same time or they will stop running in node-fetch scenarios
        responseClone.text().then(
          (r) => (responseText = r),
          () => {},
        ),
      ])
      if (handleResponseError) throw handleResponseError
    } catch (e) {
      throw new UnhandledApiError({
        status: ApiFailure.RESPONSE_PARSING_ERROR,
        message: String(e),
        meta,
      })
    }

    const isValidResponseStatus = validateStatus(response, resultData)

    if (!isValidResponseStatus) {
      if (errorSchemaValidator) {
        handleSchemaValidator(
          errorSchemaValidator,
          resultData,
          throwOnValidationError,
        )
      }

      return Err({
        status: response.status,
        data: resultData,
        meta,
      }) as Result<S, FetcherErrorResponse<E>>
    }

    if (successSchemaValidator) {
      handleSchemaValidator(
        successSchemaValidator,
        resultData,
        throwOnValidationError,
      )
    }
    return Ok({
      ...resultData,
      meta,
    }) as Result<S, FetcherErrorResponse<E>>
  }
}

function handleSchemaValidator(
  schemaValidator: ZodObject<any> | ZodString | null,
  resultData: unknown,
  throwOnValidationError: boolean,
) {
  if (!schemaValidator) return

  if (throwOnValidationError) {
    // will throw a ZodError
    schemaValidator.parse(resultData)
  } else {
    const safeResultData = schemaValidator.safeParse(resultData)
    if (!safeResultData.success) {
      console.error(prettifyError(safeResultData.error))
    }
  }
}

export { createFetcher }
export type { CreateFetcherArgs, FetcherArgs, FetcherErrorResponse }
